import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import styles from './Topbar.module.css';

const MODAL_STEP_SEND = 'SEND';
const MODAL_STEP_VERIFY = 'VERIFY';
const MODAL_STEP_CHANGE = 'CHANGE';

const Topbar = ({ collapsed, pageTitle, onMobileMenuOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(MODAL_STEP_SEND);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const otpRefs = useRef([]);
  const dropdownRef = useRef(null);

  const topbarLeft = collapsed
    ? 'var(--sidebar-collapsed-width)'
    : 'var(--sidebar-width)';

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openChangePassword = () => {
    setDropdownOpen(false);
    setModalOpen(true);
    setModalStep(MODAL_STEP_SEND);
    setError('');
    setSuccess('');
    setOtp(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
  };

  const closeModal = () => {
    setModalOpen(false);
    setError('');
    setSuccess('');
  };

  // Send OTP
  const handleSendOtp = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.sendOtp({ email: user.email, purpose: 'CHANGE_PASSWORD' });
      if (res.success) {
        setModalStep(MODAL_STEP_VERIFY);
      } else {
        setError(res.message);
      }
    } catch {
      setError('Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  // OTP handlers
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length < 6) { setError('Enter complete 6-digit OTP.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.verifyOtp({ email: user.email, otp: otpString, purpose: 'CHANGE_PASSWORD' });
      if (res.success) {
        setModalStep(MODAL_STEP_CHANGE);
      } else {
        setError(res.message);
      }
    } catch {
      setError('Failed to verify OTP.');
    } finally {
      setLoading(false);
    }
  };

  // Change Password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.changePassword({
        email: user.email,
        otp: otp.join(''),
        newPassword,
        confirmPassword,
      });
      if (res.success) {
        setSuccess('Password changed successfully!');
        setTimeout(() => closeModal(), 1500);
      } else {
        setError(res.message);
      }
    } catch {
      setError('Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header
        className={styles.topbar}
        style={{ left: topbarLeft, width: `calc(100% - ${topbarLeft})` }}
      >
        <div className={styles.left}>
          <button className={styles.menuBtn} onClick={onMobileMenuOpen}>☰</button>
          <span className={styles.pageTitle}>{pageTitle}</span>
        </div>

        <div className={styles.right}>
          {/* Search */}
          <div className={styles.searchBar}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search..."
            />
          </div>

          {/* Profile */}
          <div className={styles.profileWrapper} ref={dropdownRef}>
            <button
              className={styles.profileBtn}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div className={styles.avatar}>{getInitials(user?.name)}</div>
              <div className={styles.profileInfo}>
                <div className={styles.profileName}>
                  {user?.name?.split(' ').slice(0, 2).join(' ')}
                </div>
                <div className={styles.profileRole}>{user?.department}</div>
              </div>
              <span className={`${styles.chevron} ${dropdownOpen ? styles.chevronOpen : ''}`}>
                ▼
              </span>
            </button>

            {/* Dropdown */}
            {dropdownOpen && (
              <div className={styles.dropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.dropdownName}>{user?.name}</div>
                  <div className={styles.dropdownEmail}>{user?.email}</div>
                  <span className={styles.dropdownBadge}>{user?.branch}</span>
                </div>

                <div className={styles.dropdownDivider} />

                <button className={styles.dropdownItem} onClick={() => setDropdownOpen(false)}>
                  <span className={styles.dropdownItemIcon}>👤</span>
                  My Profile
                </button>

                <button className={styles.dropdownItem} onClick={openChangePassword}>
                  <span className={styles.dropdownItemIcon}>🔑</span>
                  Change Password
                </button>

                <div className={styles.dropdownDivider} />

                <button className={`${styles.dropdownItem} ${styles.danger}`} onClick={handleLogout}>
                  <span className={styles.dropdownItemIcon}>🚪</span>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Change Password Modal */}
      {modalOpen && (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>
                {modalStep === MODAL_STEP_SEND && 'Change Password'}
                {modalStep === MODAL_STEP_VERIFY && 'Verify OTP'}
                {modalStep === MODAL_STEP_CHANGE && 'Set New Password'}
              </span>
              <button className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>

            <div className={styles.modalBody}>
              {error && <div className={styles.modalError}>{error}</div>}
              {success && <div className={styles.modalSuccess}>{success}</div>}

              {/* Step 1 - Send OTP */}
              {modalStep === MODAL_STEP_SEND && (
                <p style={{ fontSize: '13px', color: 'var(--color-text-light)', lineHeight: 1.6 }}>
                  We will send an OTP to <strong>{user?.email}</strong> to verify your identity before changing your password.
                </p>
              )}

              {/* Step 2 - Verify OTP */}
              {modalStep === MODAL_STEP_VERIFY && (
                <div className={styles.otpStep}>
                  <p style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>
                    Enter the 6-digit OTP sent to <strong>{user?.email}</strong>
                  </p>
                  <div className={styles.otpInputRow}>
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (otpRefs.current[i] = el)}
                        className={styles.otpBox}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      />
                    ))}
                  </div>
                  <button className={styles.resendLink} onClick={handleSendOtp}>
                    Resend OTP
                  </button>
                </div>
              )}

              {/* Step 3 - Change Password */}
              {modalStep === MODAL_STEP_CHANGE && (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>New Password</label>
                    <div className={styles.inputWrapper}>
                      <input
                        className={styles.input}
                        type={showNew ? 'text' : 'password'}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <span className={styles.inputIcon} onClick={() => setShowNew(!showNew)}>
                        {showNew ? '🙈' : '👁'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Confirm Password</label>
                    <div className={styles.inputWrapper}>
                      <input
                        className={styles.input}
                        type={showConfirm ? 'text' : 'password'}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <span className={styles.inputIcon} onClick={() => setShowConfirm(!showConfirm)}>
                        {showConfirm ? '🙈' : '👁'}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={closeModal}>Cancel</button>
              {modalStep === MODAL_STEP_SEND && (
                <button className={styles.submitBtn} onClick={handleSendOtp} disabled={loading}>
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              )}
              {modalStep === MODAL_STEP_VERIFY && (
                <button className={styles.submitBtn} onClick={handleVerifyOtp} disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
              )}
              {modalStep === MODAL_STEP_CHANGE && (
                <button className={styles.submitBtn} onClick={handleChangePassword} disabled={loading}>
                  {loading ? 'Saving...' : 'Change Password'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Topbar;